// apps/frontend/src/components/common/ErrorState.tsx
// Composant d'erreur Glowp réutilisable. 6 variants avec messaging
// inclusif (mode + créateurs + seconde main + art) et palette 100% Glowp.
// Pas d'emoji — icônes Lucide animées (float + sparkles staggered).

import React, { useEffect, useState } from 'react';
import {
  Sparkles,
  Search,
  WifiOff,
  Wrench,
  Lock,
  RefreshCw,
  Home,
  ArrowLeft,
  Compass,
  Coffee,
  PackageSearch,
  Gamepad2,
} from 'lucide-react';
import GlowpCatchGame from './GlowpCatchGame';

import { glowpNavigate } from '../../lib/navigate';
export type ErrorVariant =
  | 'not-found'
  | 'crashed'
  | 'network'
  | 'server'
  | 'forbidden'
  | 'empty-search';

interface ErrorStateProps {
  variant: ErrorVariant;
  /** Override le titre par défaut du variant */
  title?: string;
  /** Override le sous-titre par défaut du variant */
  description?: string;
  /** Erreur technique optionnelle, affichée discrètement en bas */
  technicalDetail?: string;
  /** Action principale (bouton plein) */
  primaryAction?: { label: string; onClick: () => void };
  /** Action secondaire (bouton ghost) */
  secondaryAction?: { label: string; onClick: () => void };
  /** Cache le bouton "Retour à l'accueil" automatique */
  hideHomeButton?: boolean;
}

interface VariantConfig {
  title: string;
  description: string;
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  /** Léger accent visuel pour différencier d'un coup d'oeil sans sortir du brand */
  accent: 'cream' | 'sand' | 'gold' | 'deep';
}

// Palette 100% Glowp — variations sur le beige/doré uniquement.
const ACCENT_STYLES: Record<
  VariantConfig['accent'],
  { gradient: string; halo: string; iconColor: string; titleClass: string }
> = {
  cream: {
    gradient: 'linear-gradient(135deg, #F5EFE6 0%, #E8DAC7 100%)',
    halo: 'radial-gradient(circle, rgba(200,168,130,0.25) 0%, transparent 70%)',
    iconColor: '#B89968',
    titleClass: 'text-brand-700',
  },
  sand: {
    gradient: 'linear-gradient(135deg, #EFE3D2 0%, #D9C5A6 100%)',
    halo: 'radial-gradient(circle, rgba(184,153,104,0.3) 0%, transparent 70%)',
    iconColor: '#8B6914',
    titleClass: 'text-brand-700',
  },
  gold: {
    gradient: 'linear-gradient(135deg, #E8DAC7 0%, #C8A882 100%)',
    halo: 'radial-gradient(circle, rgba(139,105,20,0.3) 0%, transparent 70%)',
    iconColor: '#8B6914',
    titleClass: 'text-brand-700',
  },
  deep: {
    gradient: 'linear-gradient(135deg, #D9C5A6 0%, #B89968 100%)',
    halo: 'radial-gradient(circle, rgba(139,105,20,0.4) 0%, transparent 70%)',
    iconColor: '#FFFFFF',
    titleClass: 'text-brand-700',
  },
};

const VARIANTS: Record<ErrorVariant, VariantConfig> = {
  'not-found': {
    title: 'Cette pièce a disparu de la collection',
    description:
      "L'article, la création ou la page que tu cherches a été déplacé, vendu, ou n'a jamais existé. Pas de panique, il y a tellement à découvrir ailleurs.",
    Icon: PackageSearch,
    accent: 'cream',
  },
  crashed: {
    title: "Ça ne s'assemble pas comme prévu",
    description:
      "On a eu un petit accroc en chargeant ta page. Un nouvel essai et c'est reparti — ton travail n'est pas perdu.",
    Icon: Sparkles,
    accent: 'sand',
  },
  network: {
    title: 'Connexion en sourdine',
    description:
      "Internet semble s'être absenté un instant. Vérifie ta connexion et on reprend dès qu'elle revient.",
    Icon: WifiOff,
    accent: 'cream',
  },
  server: {
    title: 'Notre atelier prend une pause',
    description:
      'Nos serveurs ont besoin de quelques secondes pour reprendre leurs esprits. Réessaie dans un instant.',
    Icon: Coffee,
    accent: 'gold',
  },
  forbidden: {
    title: 'Cet espace est réservé',
    description:
      "Tu n'as pas les droits pour accéder à cette page. Si tu pensais en avoir, reconnecte-toi.",
    Icon: Lock,
    accent: 'deep',
  },
  'empty-search': {
    title: 'Rien dans cette sélection pour le moment',
    description:
      "Aucun résultat ici, mais Glowp s'enrichit chaque jour. Élargis ta recherche, change de catégorie, ou reviens un peu plus tard.",
    Icon: Compass,
    accent: 'cream',
  },
};

const Illustration: React.FC<{
  Icon: VariantConfig['Icon'];
  accent: VariantConfig['accent'];
}> = ({ Icon, accent }) => {
  const style = ACCENT_STYLES[accent];
  const [sparkleStep, setSparkleStep] = useState(0);

  // Sparkles qui apparaissent en cascade puis se reset
  useEffect(() => {
    const t = setInterval(() => setSparkleStep(s => (s + 1) % 4), 2000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative w-32 h-32 mx-auto mb-6">
      {/* Halo doux animé */}
      <div
        className="absolute inset-0 rounded-full animate-pulse"
        style={{ background: style.halo, animationDuration: '3s' }}
      />

      {/* Cercle central avec gradient brand */}
      <div
        className="absolute inset-2 rounded-full shadow-inner"
        style={{ background: style.gradient }}
      />

      {/* Icône principale qui flotte doucement */}
      <div className="absolute inset-0 flex items-center justify-center animate-[gentleFloat_3s_ease-in-out_infinite]">
        <Icon className="w-12 h-12" strokeWidth={1.5} />
      </div>

      {/* Sparkles staggered autour de l'icône */}
      {[
        { top: '-4px', right: '8px', delay: 0 },
        { bottom: '0px', left: '4px', delay: 1 },
        { top: '20%', left: '-6px', delay: 2 },
        { bottom: '20%', right: '-4px', delay: 3 },
      ].map((s, i) => (
        <div
          key={i}
          className="absolute transition-all duration-700"
          style={{
            ...s,
            opacity: sparkleStep === i ? 1 : 0,
            transform: sparkleStep === i ? 'scale(1)' : 'scale(0.5)',
          }}
        >
          <Sparkles className="w-3.5 h-3.5" style={{ color: '#C8A882' }} />
        </div>
      ))}

      {/* Override icon color via CSS variable since Lucide accepte color via style/className */}
      <style>{`
        @keyframes gentleFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
};

// Wrapper qui injecte la couleur de l'icône via context CSS
const IllustrationStyled: React.FC<{
  Icon: VariantConfig['Icon'];
  accent: VariantConfig['accent'];
}> = ({ Icon, accent }) => {
  const style = ACCENT_STYLES[accent];

  return (
    <div style={{ color: style.iconColor }}>
      <Illustration Icon={Icon} accent={accent} />
    </div>
  );
};

export const ErrorState: React.FC<ErrorStateProps> = ({
  variant,
  title,
  description,
  technicalDetail,
  primaryAction,
  secondaryAction,
  hideHomeButton = false,
}) => {
  // Pas d'useNavigate ici : ErrorState peut être rendu hors d'un Router
  // (typiquement par un ErrorBoundary qui catch une erreur AVANT que
  // RouterProvider soit monté). On utilise window.location pour rester safe.
  const config = VARIANTS[variant];
  const accent = ACCENT_STYLES[config.accent];
  const [showGame, setShowGame] = useState(false);

  // Le mini-jeu n'a de sens que sur les cas où l'user attend ("en pause" ou
  // potentiellement long) — pas sur 404 ni forbidden où l'action est immédiate.
  const offerGame =
    variant === 'crashed' || variant === 'network' || variant === 'server';

  const goHome = () => {
    // HashRouter → on doit reset à la racine du hash, pas à `/`
    glowpNavigate('#/');
    // Si on est déjà sur la home et le hash ne change pas, force un reload léger
    if (window.location.hash === '#/' || window.location.hash === '') {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-10">
      <div className="bg-white rounded-3xl shadow-sm ring-1 ring-black/5 p-8 sm:p-10 max-w-md w-full text-center animate-[fadeIn_0.4s_ease-out]">
        <IllustrationStyled Icon={config.Icon} accent={config.accent} />

        <h2
          className={`text-xl sm:text-2xl font-bold mb-2 ${accent.titleClass}`}
        >
          {title || config.title}
        </h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          {description || config.description}
        </p>

        <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 transition-colors shadow-sm"
            >
              <RefreshCw className="w-4 h-4" />
              {primaryAction.label}
            </button>
          )}
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              {secondaryAction.label}
            </button>
          )}
          {!hideHomeButton && !primaryAction && (
            <button
              onClick={goHome}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 transition-colors shadow-sm"
            >
              <Home className="w-4 h-4" />
              Retour à l'accueil
            </button>
          )}
          {!hideHomeButton && primaryAction && (
            <button
              onClick={goHome}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Accueil
            </button>
          )}
        </div>

        {/* Mini-jeu en attendant — seulement sur les variants où ça a du sens */}
        {offerGame && (
          <div className="mt-6">
            {!showGame ? (
              <button
                onClick={() => setShowGame(true)}
                className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-brand-700 transition-colors"
              >
                <Gamepad2 className="w-3.5 h-3.5" />
                Mini-pause en attendant
              </button>
            ) : (
              <GlowpCatchGame onClose={() => setShowGame(false)} />
            )}
          </div>
        )}

        {technicalDetail && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
              Détails techniques
            </summary>
            <pre className="mt-2 text-[10px] text-gray-500 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
              {technicalDetail}
            </pre>
          </details>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// Fallback explicite pour le linter / tree-shaking — Wrench gardé en réserve
// pour un futur variant 'maintenance' / 'feature-soon'.
void Wrench;
void Search;

export default ErrorState;
